import { timeToMinutes } from './time-utils'
import { TIME_CONSTANTS } from './time-constants'

/**
 * Determines if the given time range represents overnight hours
 */
export function isOvernightHours(opens: string, closes: string): boolean {
  if (!opens || !closes || (opens === TIME_CONSTANTS.MIDNIGHT_00H && closes === TIME_CONSTANTS.MIDNIGHT_00H)) {
    return false
  }
  
  const openMinutes = timeToMinutes(opens)
  const closeMinutes = timeToMinutes(closes)

  if (Number.isNaN(openMinutes) || Number.isNaN(closeMinutes)) {
    return false
  }
  
  return closeMinutes < openMinutes
}

/**
 * Calculates the duration of overnight hours in minutes
 */
export function getOvernightDuration(opens: string, closes: string): number {
  if (!isOvernightHours(opens, closes)) {
    return 0
  }
  
  const openMinutes = timeToMinutes(opens)
  const closeMinutes = timeToMinutes(closes)
  
  if (closes.startsWith('24:')) {
    return (TIME_CONSTANTS.HOURS_PER_DAY * TIME_CONSTANTS.MINUTES_PER_HOUR) - openMinutes + 
           (closeMinutes - (TIME_CONSTANTS.HOURS_PER_DAY * TIME_CONSTANTS.MINUTES_PER_HOUR))
  }
  
  return (TIME_CONSTANTS.HOURS_PER_DAY * TIME_CONSTANTS.MINUTES_PER_HOUR) - openMinutes + closeMinutes
}

/**
 * Splits overnight hours into evening and morning periods
 */
export function splitOvernightHours(opens: string, closes: string): { evening: { opens: string, closes: string }, morning: { opens: string, closes: string } } {
  if (!isOvernightHours(opens, closes)) {
    throw new Error('Cannot split non-overnight hours')
  }
  
  return {
    evening: {
      opens,
      closes: TIME_CONSTANTS.MIDNIGHT_24H
    },
    morning: {
      opens: TIME_CONSTANTS.MIDNIGHT_00H,
      closes: closes.startsWith('24:') ? closes.replace('24:', '00:') : closes
    }
  }
}

/**
 * Checks if a time range spans midnight (helper for overnight detection)
 */
export function spansMidnight(opens: string, closes: string): boolean {
  return isOvernightHours(opens, closes)
}

/**
 * Merges overnight periods that were split for schema.org compliance
 * Looks for patterns where one slot ends at 23:59 and the next day starts at 00:00
 * @param slots - Array of time slots organized by day
 * @returns Modified slots with overnight periods merged
 */
export function mergeOvernightPeriods(dayMap: Record<string, any>): void {
  const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
  
  Object.keys(dayMap).forEach(day => {
    const currentDay = dayMap[day]
    const currentDayIndex = DAY_ORDER.indexOf(day)
    const nextDayIndex = (currentDayIndex + 1) % 7
    const nextDay = DAY_ORDER[nextDayIndex]
    
    // Look for overnight pattern: any slot that could be part of an overnight period
    currentDay.slots.forEach((slot: any) => {
      // Check if this slot could be the evening part of an overnight period
      const [closeHour, closeMin] = slot.closes.split(':').map(Number)
      
      // Check if slot ends at 23:59 (potential overnight pattern)
      const closesLate = closeHour === 23 && closeMin === 59
      
      if (closesLate && dayMap[nextDay]) {
        // Look for a slot on the next day that starts at 00:00
        const earlyNextSlot = dayMap[nextDay].slots.find((nextSlot: any) =>
          nextSlot.opens === '00:00' && nextSlot.closes !== '00:00'
        )
        
        if (earlyNextSlot) {
          // Merge overnight period: extend current day's closing time
          slot.closes = earlyNextSlot.closes
          
          // Remove the early slot from next day
          dayMap[nextDay].slots = dayMap[nextDay].slots.filter((nextSlot: any) => nextSlot !== earlyNextSlot)
        }
      }
    })
  })
}
